/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */
import * as React from 'react';
import { compose, withHandlers, withState } from 'recompose';
import { Grid, Row, Col } from 'react-flexbox-grid';
import { withNotifications } from '@components/hoc/withNotifications';
import Button from '@inqool/uas-web-core/es/components/controls/Button';
import { reduxForm } from 'redux-form';
import { InputField, Input, DateField, Date } from '@components/form';
import { externalLookupIszr } from '@modules/wantedPersons/_wantedPersonsActions';
import { pcrPersonSearchResult, pcrPersonWarnings, pcrSearchResult, sexEnum } from './_personEnums';
import { get } from 'lodash';
import * as moment from 'moment';
import { buildFullAddress } from '@utils/utils';
import { required } from '@utils/validators';

const validateData = ({ firstName, lastName, birthDate, citizenship, handleFormErrorMessage }) => {
  if (!(firstName && lastName && birthDate && citizenship)) {
    handleFormErrorMessage('Vyplňte všechny hodnoty');
    return false;
  }
  if (citizenship.n3.length > 3) {
    handleFormErrorMessage('Státní příslušnost: maximálně 3 znaky');
    return false;
  }
  handleFormErrorMessage(undefined);
  return true;
};

const enhance = compose<any, any>(
  withState('showError', 'handleShowError', false),
  withState('formErrorMessage', 'handleFormErrorMessage', undefined),
  withState('person', 'handlePerson', undefined),
  withNotifications({ externalLookupIszr }),
  withHandlers({
    onSubmit: ({
      handleLoader,
      externalLookupIszrNotify,
      handleShowError,
      handlePerson,
      handleFormErrorMessage
    }) => async formData => {
      handleLoader(true);
      const isValid = validateData({ ...formData, handleFormErrorMessage });

      handleShowError(!isValid);

      if (!isValid) {
        handleLoader(false);
        return;
      }

      const response = await externalLookupIszrNotify(formData);

      if (response) {
        handlePerson(response);
      }

      handleLoader(false);
    },
    handleFill: ({ closeItem, withPhoto, changeFieldValue, changePhotoBase64, person }) => () => {
      for (const attribute of Object.keys(person.person)) {
        changeFieldValue(attribute, person.person[attribute]);
      }

      if (withPhoto && person.photo) {
        changePhotoBase64(person.photo);
      }

      closeItem();
    }
  }),
  reduxForm({
    form: 'module-pcr-iszr-lookup'
  })
);

const View = ({ closeItem, showError, handleSubmit, person, handleFill, formErrorMessage }) => (
  <div style={{ margin: '15px' }}>
    <form className="layout" onSubmit={handleSubmit}>
      <Grid fluid={true}>
        <Row>
          <Col className="grid-system-margin" />
        </Row>
        <Row>
          <h4>Pátrání PČR</h4>
        </Row>
        <Row>
          {[{ label: 'Jméno *', name: 'firstName' }, { label: 'Příjmení *', name: 'lastName' }].map(
            ({ label, name }) => (
              <Col md={4} key={name}>
                <InputField component={Input} name={name} label={label} validate={required} />
              </Col>
            )
          )}
          <Col md={4}>
            <DateField component={Date} name={'birthDate'} label={'Datum narození *'} validate={required} />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <InputField component={Input} name={'citizenship.n3'} label={'Státní příslušnost *'} validate={required} />
          </Col>
        </Row>

        {showError && (
          <Row>
            <h5 style={{ color: 'red' }}>{formErrorMessage}</h5>
          </Row>
        )}

        {person && (
          <>
            <Row>
              <h4>{get(pcrSearchResult.find(o => o.id === person.searchResult), 'label')}</h4>
            </Row>

            <Row>
              {person.photo && (
                <Col md={3} style={{ marginRight: 20 }}>
                  <img
                    src={`data:image/png;base64, ${person.photo}`}
                    style={{ objectFit: 'contain', maxWidth: '100%' }}
                  />
                </Col>
              )}

              <Col md={person.photo ? 4 : 6}>
                <Row>
                  <Col>
                    <b>Jméno:</b> {get(person, 'person.firstName')}
                  </Col>
                </Row>
                <Row>
                  <Col className="grid-system-margin" />
                </Row>
                <Row>
                  <Col>
                    <b>Příjmení:</b> {get(person, 'person.lastName')}
                  </Col>
                </Row>
                <Row>
                  <Col className="grid-system-margin" />
                </Row>
                <Row>
                  <Col>
                    <b>Rodné číslo:</b> {get(person, 'person.birthNumber')}
                  </Col>
                </Row>
                <Row>
                  <Col className="grid-system-margin" />
                </Row>
                <Row>
                  <Col>
                    <b>Datum narození:</b> {moment(get(person, 'person.birthDate')).format('DD.MM.YYYY')}
                  </Col>
                </Row>
                <Row>
                  <Col className="grid-system-margin" />
                </Row>
                <Row>
                  <Col>
                    <b>Pohlaví:</b> {get(sexEnum.find(o => o.id === person.person.sex), 'label')}
                  </Col>
                </Row>
              </Col>

              <Col md={person.photo ? 4 : 6}>
                <Row>
                  <Col>
                    <b>Telefonní číslo:</b> {get(person, 'person.phone')}
                  </Col>
                </Row>
                <Row>
                  <Col className="grid-system-margin" />
                </Row>
                <Row>
                  <Col>
                    <b>Adresa:</b>{' '}
                    {get(person, 'person.address') ? buildFullAddress(get(person, 'person.address')) : ''}
                  </Col>
                </Row>
                <Row>
                  <Col className="grid-system-margin" />
                </Row>
                <Row>
                  <Col>
                    <b>Výsledek hledání:</b>{' '}
                    {get(pcrPersonSearchResult.find(o => o.id === person.personSearchResult), 'label')}
                  </Col>
                </Row>
                <Row>
                  <Col className="grid-system-margin" />
                </Row>
                <Row>
                  <Col>
                    <b>Varování:</b>{' '}
                    {person.personWarnings.map(v => get(pcrPersonWarnings.find(o => o.id === v), 'label')).join(', ')}
                  </Col>
                </Row>
                <Row>
                  <Col className="grid-system-margin" />
                </Row>
                <Row>
                  <Col>
                    <b>Instrukce:</b> {get(person, 'instruction')}
                  </Col>
                </Row>
              </Col>
            </Row>
          </>
        )}
        <Row>
          <Col className="grid-system-margin" />
        </Row>
      </Grid>

      {person ? (
        <Button label={'Uložit a zavřít'} onClick={handleFill} primary={true} style={{ margin: '15px' }} />
      ) : (
        <Button label={'Hledat'} type="submit" primary={true} style={{ margin: '15px' }} />
      )}
      <Button label={'Zavřít'} onClick={closeItem} style={{ marginTop: '15px' }} />
    </form>
  </div>
);

export const PcrPersonForm = enhance(View);



// WEBPACK FOOTER //
// ./src/components/person/PcrPersonForm.tsx